import { Component, screen, warn, _decorator } from 'cc';
import { BUILD } from 'cc/env';
import I18n from '../../../common/scripts/I18n/I18n';
import OrientationManager from '../../../common/scripts/OrientationManager';
import BindDOMNode from '../../../common/scripts/Toolkit/Components/DOM/BindDOMNode';
import MultiThemeSource from '../../../common/scripts/Toolkit/Components/MultiTheme/MultiThemeSource';
import { fillTextArgument } from '../../../common/scripts/Toolkit/Utils/Utility';
import TableGame from '../System/TableGame';

const { ccclass, property } = _decorator;
const offsetHeight = 300;
const MESSAGE_IMAGE_PATH_TEMPLATE = 'images/message/img_swipeFullScreen_{0}.png';

@ccclass('FullscreenGestureController')
export default class FullscreenGestureController extends Component {
    public static readonly EVENT_TOOLBAR_COLLAPSED_STATE_CHANGED = 'EVENT_TOOLBAR_COLLAPSED_STATE_CHANGED';

    @property(BindDOMNode)
    private readonly blackMask: BindDOMNode = null;

    @property(BindDOMNode)
    private readonly hintMessage: BindDOMNode = null;

    @property(BindDOMNode)
    private readonly hideGestureButton: BindDOMNode = null;

    @property(MultiThemeSource)
    private readonly orientationThemeSource: MultiThemeSource = null;

    private _disabled = false;
    private _isToolBarCollapsed = false;
    private _blackMaskElement: HTMLElement = null;
    private _hintMessageElement: HTMLImageElement = null;
    private _hideGestureButtonElement: HTMLImageElement = null;
    private _testHeightObject: HTMLElement = null;

    public get disabled() {
        return this._disabled;
    }

    public get isToolBarCollapsed() {
        return this._isToolBarCollapsed;
    }
    private set isToolBarCollapsed(value: boolean) {
        if (this._isToolBarCollapsed === value) {
            return;
        }
        // once toolbar is collapsed, disallow gesture to be shown until user decides to show it again
        if (value) {
            this._hideGestureIcon();
        }
        this._isToolBarCollapsed = value;
        this.node.emit(FullscreenGestureController.EVENT_TOOLBAR_COLLAPSED_STATE_CHANGED, value);
    }

    private get testHeightObject() {
        if (!this._testHeightObject) {
            this._testHeightObject = document.querySelector('.testHeightObject');
        }
        return this._testHeightObject;
    }

    private get isBrowserLandscape() {
        return window.innerWidth > window.innerHeight;
    }

    public onLoad() {
        const agent = window.navigator.userAgent.toLowerCase();
        const isIOS = agent && /iphone|ipad|ipod|ios|os/.test(agent);
        const isIframe = !!window.parent && window !== window.parent;
        // if fullscreen is supported, then no need to use gesture to collapse toolbar
        // only use gesture to collapse toolbar in iOS and not in iframe
        if (screen.supportsFullScreen || !isIOS || isIframe || !BUILD) {
            this._disabled = true;
            return;
        }
        this._blackMaskElement = this.blackMask.element as HTMLElement;
        this._hintMessageElement = this.hintMessage.element as HTMLImageElement;
        this._hideGestureButtonElement = this.hideGestureButton.element as HTMLImageElement;

        this._hideGestureButtonElement.addEventListener('click', (event) => {
            event.preventDefault();
            this._hideGestureIcon();
        });
        TableGame.orientation.on(OrientationManager.EVENT_TYPE.RESIZE, this._onResize, this);
        TableGame.orientation.on(OrientationManager.EVENT_TYPE.ORIENTATION_CHANGE, this._onResize, this);
        TableGame.i18n.on(I18n.EVENT_TYPE.LanguageChanged, this._updateMessageSpriteFrame, this);
    }

    public start() {
        if (this.disabled) {
            return;
        }
        this._blackMaskElement.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
        this._updateMessageSpriteFrame();
    }

    public onEnable() {
        if (this.disabled) {
            return;
        }
        this._onResize();
    }

    public showGesture() {
        if (this._checkIfToolBarCollapsed() || this.disabled) {
            return;
        }
        window.canShowGesture = true;
        this.blackMask.node.active = true;
        // allow user to scroll when gesture is shown
        document.body.style.overflow = 'scroll';
    }

    private _hideGestureIcon() {
        window.canShowGesture = false;
        this.blackMask.node.active = false;
        // disallow user to scroll when gesture is hidden
        document.body.style.overflow = 'hidden';
    }

    private _onResize() {
        this._checkIfToolBarCollapsed();
        if (!this.isBrowserLandscape) {
            this.orientationThemeSource.theme = 0;
        } else if (window.orientation === 90) {
            this.orientationThemeSource.theme = 1;
        } else if (window.orientation === -90) {
            this.orientationThemeSource.theme = 2;
        } else {
            warn(`Unknown orientation: ${window.orientation}`);
            this.orientationThemeSource.theme = 0;
        }
    }

    private _checkIfToolBarCollapsed(): boolean {
        const scrollTop = document.querySelector('html').scrollTop || window.scrollY || document.body.scrollTop;
        const isToolBarCollapsed = this.testHeightObject ? !(this.testHeightObject.clientHeight - window.innerHeight > 20 || scrollTop < offsetHeight) : false;
        this.isToolBarCollapsed = isToolBarCollapsed;
        return isToolBarCollapsed;
    }

    private _updateMessageSpriteFrame() {
        const path = fillTextArgument(MESSAGE_IMAGE_PATH_TEMPLATE, [TableGame.i18n.currentLanguage]);
        this._hintMessageElement.src = path;
    }
}
